import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_DISPLAY_CURRENCY,
  FxRateUnavailableError,
  formatMoney,
  isCurrency,
  todayIso,
  type Currency,
  type IsoDate,
  type Money,
} from '@budget/core';
import { useAuth } from '../auth/auth-context.js';
import { CurrencyContext, type CurrencyState, type FxStatus } from './currency-context.js';
import { useFxRates } from './use-fx-rates.js';

const STORAGE_KEY = 'budget.displayCurrency';

function readStoredCurrency(): Currency {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isCurrency(stored)) return stored;
  } catch {
    // Private mode or blocked storage — fall through to the default.
  }
  return DEFAULT_DISPLAY_CURRENCY;
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [displayCurrency, setDisplayCurrencyState] = useState<Currency>(readStoredCurrency);
  const { status } = useAuth();
  // `isError` is intentionally not read: anything that is not a loaded table
  // and not still loading is an error state as far as the UI is concerned,
  // including a query that succeeded but returned no rates at all.
  const { fxTable, isPending } = useFxRates({ enabled: status === 'authenticated' });

  const setDisplayCurrency = useCallback((currency: Currency) => {
    setDisplayCurrencyState(currency);
    try {
      window.localStorage.setItem(STORAGE_KEY, currency);
    } catch {
      // Not being able to remember the choice is not worth failing the switch over.
    }
  }, []);

  const value = useMemo<CurrencyState>(() => {
    const fxStatus: FxStatus = fxTable ? 'ready' : isPending ? 'loading' : 'error';

    const convert = (money: Money, on?: IsoDate): Money => {
      if (money.currency === displayCurrency) return money;
      if (!fxTable) throw new FxRateUnavailableError(money.currency, on ?? todayIso());

      return fxTable.convert(money, displayCurrency, on ?? fxTable.latestDate() ?? todayIso());
    };

    const tryConvert = (money: Money, on?: IsoDate): Money | null => {
      try {
        return convert(money, on);
      } catch (error) {
        if (error instanceof FxRateUnavailableError) return null;
        throw error;
      }
    };

    return {
      displayCurrency,
      setDisplayCurrency,
      fxTable,
      fxStatus,
      convert,
      tryConvert,
      // A single cell carries its own currency label, so showing the original
      // amount is honest here even though it would not be in a total.
      format: (money, on) => formatMoney(tryConvert(money, on) ?? money),
    };
  }, [displayCurrency, setDisplayCurrency, fxTable, isPending]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}
